/**
 * Ticket API Service
 * @description Backend 티켓 API 연동
 */

import type {
    Ticket,
    TicketListParams,
    TicketListResponse,
    CreateTicketRequest,
    UpdateTicketRequest,
    ChangeStatusRequest,
    AssignTicketRequest,
    CloseTicketRequest,
    ApprovalActionRequest,
} from '@/types/ticket.types';
import { apiClient } from '@/utils/httpClient';
import { getTenantAuthStorage } from '@/utils/authStorage';

/**
 * 헤더에 Tenant ID 추가하는 헬퍼 함수
 */
function getTenantHeaders(tenantId?: string) {
    const user = getTenantAuthStorage().getUser();
    const actualTenantId = tenantId ?? (user?.userType === 'tenant-user' ? user.tenantId : undefined);
    return (actualTenantId ? { 'X-Tenant-Id': actualTenantId } : {}) as Record<string, string>;
}

/**
 * 티켓 목록 조회
 */
export async function getTickets(
    tenantId: string,
    params?: TicketListParams
): Promise<TicketListResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });
    }

    const queryString = queryParams.toString();
    const endpoint = `/tenants/${tenantId}/tickets${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<TicketListResponse>(endpoint, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 티켓 상세 조회
 */
export async function getTicket(
    tenantId: string,
    ticketId: string
): Promise<Ticket> {
    return apiClient.get<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}`, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 티켓 생성
 */
export async function createTicket(
    tenantId: string,
    data: CreateTicketRequest
): Promise<Ticket> {
    return apiClient.post<Ticket>(`/tenants/${tenantId}/tickets`, data, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 티켓 수정
 */
export async function updateTicket(
    tenantId: string,
    ticketId: string,
    data: UpdateTicketRequest
): Promise<Ticket> {
    return apiClient.request<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 티켓 상태 변경
 */
export async function changeTicketStatus(
    tenantId: string,
    ticketId: string,
    data: ChangeStatusRequest
): Promise<Ticket> {
    return apiClient.request<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 담당자 할당
 */
export async function assignTicket(
    tenantId: string,
    ticketId: string,
    data: AssignTicketRequest
): Promise<Ticket> {
    return apiClient.post<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}/assign`, data, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 티켓 닫기
 */
export async function closeTicket(
    tenantId: string,
    ticketId: string,
    data: CloseTicketRequest
): Promise<Ticket> {
    return apiClient.post<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}/close`, data, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 승인 제출
 */
export async function submitApproval(
    tenantId: string,
    ticketId: string
): Promise<Ticket> {
    return apiClient.post<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}/approval/submit`, {}, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 승인
 */
export async function approveTicket(
    tenantId: string,
    ticketId: string,
    data?: ApprovalActionRequest
): Promise<Ticket> {
    return apiClient.post<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}/approval/approve`, data || {}, {
        headers: getTenantHeaders(tenantId),
    });
}

/**
 * 거부
 */
export async function rejectTicket(
    tenantId: string,
    ticketId: string,
    data?: ApprovalActionRequest
): Promise<Ticket> {
    return apiClient.post<Ticket>(`/tenants/${tenantId}/tickets/${ticketId}/approval/reject`, data || {}, {
        headers: getTenantHeaders(tenantId),
    });
}